import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Package, 
  Truck, 
  CheckCircle, 
  XCircle, 
  Clock,
  Download,
  MapPin,
  ChevronRight,
  Star
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { toast } from 'sonner';
import api from '../utils/api';
import { useAuth } from '../context/AuthContext';
import { getApiErrorMessage } from '../utils/errorMessage';

const MyOrders = () => {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [reviewLoadingKey, setReviewLoadingKey] = useState(null);
  const [reviewedKeys, setReviewedKeys] = useState(new Set());
  const [reviewModal, setReviewModal] = useState({
    open: false,
    orderId: '',
    orderNumber: '',
    productId: '',
    productName: '',
    variantId: '',
  });
  const [reviewForm, setReviewForm] = useState({
    rating: 0,
    comment: '',
    files: [],
  });
  const { user, loading: authLoading } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    if (authLoading) return;

    if (!user) {
      navigate('/login');
      return;
    }
    fetchOrders();
    fetchMyReviews();
  }, [user, authLoading, navigate]);

  const fetchOrders = async () => {
    try {
      setLoading(true);
      const response = await api.get('/orders/my-orders');
      setOrders(response.data);
    } catch (error) {
      toast.error('Failed to fetch orders');
    } finally {
      setLoading(false);
    }
  };

  const fetchMyReviews = async () => {
    try {
      const response = await api.get('/reviews/my');
      const keys = new Set(
        (response.data?.reviews || []).map((r) => `${r.order_id}:${r.product_id}`)
      );
      setReviewedKeys(keys);
    } catch (error) {
      setReviewedKeys(new Set());
    }
  };

  const openReviewModal = (order, item) => {
    setReviewModal({
      open: true,
      orderId: order.id,
      orderNumber: order.order_number,
      productId: item.product_id,
      productName: item.product_name,
      variantId: item.variant_id,
    });
    setReviewForm({ rating: 0, comment: '', files: [] });
  };

  const closeReviewModal = () => {
    setReviewModal({
      open: false,
      orderId: '',
      orderNumber: '',
      productId: '',
      productName: '',
      variantId: '',
    });
    setReviewForm({ rating: 0, comment: '', files: [] });
  };

  const renderStars = (value, onClick) => (
    <div className="flex items-center gap-1">
      {[1, 2, 3, 4, 5].map((v) => (
        <button
          key={v}
          type="button"
          onClick={() => onClick(v)}
          className="p-0.5"
        >
          <Star className={`h-6 w-6 ${v <= value ? 'text-amber-500 fill-amber-500' : 'text-amber-200'}`} />
        </button>
      ))}
    </div>
  );

  const handleSubmitReview = async () => {
    if (!reviewForm.rating) {
      toast.error('Rating is required');
      return;
    }
    const key = `${reviewModal.orderId}:${reviewModal.productId}`;
    try {
      setReviewLoadingKey(key);
      let imageUrls = [];
      if (reviewForm.files.length > 0) {
        for (const file of reviewForm.files) {
          const formData = new FormData();
          formData.append('file', file);
          const up = await api.post('/reviews/upload-image', formData, {
            headers: { 'Content-Type': 'multipart/form-data' },
          });
          if (up.data?.url) imageUrls.push(up.data.url);
        }
      }
      await api.post('/reviews', {
        order_id: reviewModal.orderId,
        product_id: reviewModal.productId,
        variant_id: reviewModal.variantId || null,
        rating: reviewForm.rating,
        comment: reviewForm.comment || '',
        image_urls: imageUrls,
      });
      toast.success('Review submitted successfully');
      await fetchMyReviews();
      closeReviewModal();
    } catch (error) {
      toast.error(getApiErrorMessage(error, 'Failed to submit review'));
    } finally {
      setReviewLoadingKey(null);
    }
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      'payment_pending': { color: 'bg-yellow-100 text-yellow-800', icon: Clock, label: 'Payment Pending' },
      'pending_payment': { color: 'bg-yellow-100 text-yellow-800', icon: Clock, label: 'Pending Payment' },
      'processing': { color: 'bg-blue-100 text-blue-800', icon: Package, label: 'Processing' },
      'shipped': { color: 'bg-purple-100 text-purple-800', icon: Truck, label: 'Shipped' },
      'in_transit': { color: 'bg-indigo-100 text-indigo-800', icon: Truck, label: 'In Transit' },
      'out_for_delivery': { color: 'bg-indigo-100 text-indigo-800', icon: Truck, label: 'Out for Delivery' },
      'delivered': { color: 'bg-green-100 text-green-800', icon: CheckCircle, label: 'Delivered' },
      'cancelled': { color: 'bg-red-100 text-red-800', icon: XCircle, label: 'Cancelled' }
    };
    const normalized = (status || '').toLowerCase();
    const config = statusConfig[normalized] || statusConfig['payment_pending'];
    const Icon = config.icon;
    
    return (
      <span className={`inline-flex items-center gap-1 px-3 py-1 rounded-full text-xs font-medium ${config.color}`}>
        <Icon className="h-3 w-3" />
        {config.label}
      </span>
    );
  };

  const getTrackingStatus = (order) => {
    if (order.tracking_number) {
      return (
        <div className="mt-2 p-3 bg-amber-50 rounded-lg">
          <p className="text-xs font-medium text-amber-900">Tracking Information</p>
          <div className="flex items-center gap-2 mt-1">
            <span className="text-xs text-amber-700">{order.courier_name}:</span>
            <span className="text-xs font-mono font-medium text-amber-900">{order.tracking_number}</span>
            {order.tracking_url && (
              <a
                href={order.tracking_url}
                target="_blank"
                rel="noopener noreferrer"
                className="text-xs text-amber-600 hover:text-amber-700 underline"
              >
                Track
              </a>
            )}
          </div>
          {order.estimated_delivery && (
            <p className="text-xs text-amber-600 mt-1">
              Estimated Delivery: {new Date(order.estimated_delivery).toLocaleDateString('en-IN')}
            </p>
          )}
        </div>
      );
    }
    return null;
  };

  

  if (authLoading || loading) {
    return (
      <div className="min-h-screen pt-24 pb-12 bg-gradient-to-br from-amber-50 via-white to-orange-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-16 w-16 border-4 border-amber-600 border-t-transparent mx-auto mb-4"></div>
          <p className="text-amber-700">Loading your orders...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen pt-24 pb-12 bg-gradient-to-br from-amber-50 via-white to-orange-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <h1 className="text-4xl font-bold text-amber-950 mb-8">My Orders</h1>

        {orders.length === 0 ? (
          <div className="bg-white border-2 border-amber-200 rounded-2xl p-12 text-center">
            <Package className="h-16 w-16 text-amber-400 mx-auto mb-4" />
            <h2 className="text-2xl font-semibold text-amber-950 mb-2">No orders yet</h2>
            <p className="text-amber-700 mb-6">
              Looks like you haven't placed any orders yet
            </p>
            <Button
              onClick={() => navigate('/shop')}
              className="bg-gradient-to-r from-amber-600 to-orange-600 text-white px-8 py-3"
            >
              Start Shopping
            </Button>
          </div>
        ) : (
          <div className="space-y-6">
            {orders.map((order) => (
              <div
                key={order.id}
                className="bg-white border-2 border-amber-200 rounded-2xl p-6 shadow-sm hover:shadow-md transition-shadow"
              >
                {/* Order Header */}
                <div className="flex flex-wrap justify-between items-start gap-4 mb-4 pb-4 border-b border-amber-100">
                  <div>
                    <div className="flex items-center gap-3 mb-2">
                      <span className="text-sm text-amber-600">Order #</span>
                      <span className="font-mono font-bold text-amber-950">
                        {order.order_number}
                      </span>
                      {getStatusBadge(order.order_status)}
                    </div>
                    <p className="text-xs text-amber-600">
                      Placed on {new Date(order.created_at).toLocaleDateString('en-IN', {
                        day: 'numeric',
                        month: 'long',
                        year: 'numeric',
                        hour: '2-digit',
                        minute: '2-digit'
                      })}
                    </p>
                  </div>
                  <div className="text-left sm:text-right">
                    <p className="text-sm text-amber-600">Total Amount</p>
                    <p className="text-2xl font-bold text-amber-600">
                      Rs {Number(order?.pricing_breakdown?.total ?? order?.total ?? 0).toFixed(2)}
                    </p>
                  </div>
                </div>

                {/* Order Items */}
                <div className="space-y-3">
                  {order.items.map((item) => (
                    <div key={item.variant_id} className="flex flex-col sm:flex-row gap-4">
                      <img
                        src={item.product_image || '/placeholder.png'}
                        alt={item.product_name}
                        className="w-full sm:w-16 h-40 sm:h-16 object-cover rounded-lg border border-amber-100"
                      />
                      <div className="flex-1">
                        <h3 className="font-medium text-amber-950">
                          {item.product_name}
                        </h3>
                        <p className="text-xs text-amber-700">Size: {item.variant_size}</p>
                        <div className="flex justify-between mt-1">
                          <span className="text-sm text-amber-900">
                            Qty: {item.quantity}
                          </span>
                          <span className="font-semibold text-amber-950">
                            Rs {Number(item.total ?? 0).toFixed(2)}
                          </span>
                        </div>
                        {String(order.order_status || '').toLowerCase() === 'delivered' && item.product_id && (
                          <div className="mt-2">
                            {reviewedKeys.has(`${order.id}:${item.product_id}`) ? (
                              <span className="inline-flex items-center gap-1 text-xs text-green-700 bg-green-50 px-2 py-1 rounded-full">
                                <CheckCircle className="h-3 w-3" />
                                Review Submitted
                              </span>
                            ) : (
                              <Button
                                variant="outline"
                                size="sm"
                                className="border-amber-300 text-amber-700"
                                disabled={reviewLoadingKey === `${order.id}:${item.product_id}`}
                                onClick={() => openReviewModal(order, item)}
                              >
                                <Star className="h-4 w-4 mr-1" />
                                Rate Product
                              </Button>
                            )}
                          </div>
                        )}
                      </div>
                    </div>
                  ))}
                </div>

                <div className="mt-4 pt-4 border-t border-amber-100">
                  <h4 className="text-sm font-semibold text-amber-950 mb-2">Price Breakdown</h4>
                  <div className="space-y-1 text-sm">
                    <div className="flex justify-between text-amber-700">
                      <span>Subtotal</span>
                      <span>Rs {Number(order?.pricing_breakdown?.subtotal ?? order?.subtotal ?? 0).toFixed(2)}</span>
                    </div>
                    {Number(order?.pricing_breakdown?.discount ?? order?.discount ?? 0) > 0 && (
                      <div className="flex justify-between text-green-700">
                        <span>
                          Discount
                          {order?.pricing_breakdown?.coupon_code ? ` (${order.pricing_breakdown.coupon_code})` : ''}
                        </span>
                        <span>-Rs {Number(order?.pricing_breakdown?.discount ?? order?.discount ?? 0).toFixed(2)}</span>
                      </div>
                    )}
                    <div className="flex justify-between text-amber-700">
                      <span>Shipping</span>
                      <span>
                        {Number(order?.pricing_breakdown?.shipping_fee ?? order?.shipping_fee ?? 0) === 0
                          ? 'FREE'
                          : `Rs ${Number(order?.pricing_breakdown?.shipping_fee ?? order?.shipping_fee ?? 0).toFixed(2)}`}
                      </span>
                    </div>
                    <div className="flex justify-between font-semibold text-amber-950 pt-1 border-t border-amber-100">
                      <span>Total</span>
                      <span>Rs {Number(order?.pricing_breakdown?.total ?? order?.total ?? 0).toFixed(2)}</span>
                    </div>
                  </div>
                </div>

                {/* Tracking Info */}
                {getTrackingStatus(order)}

                {/* Delivery Address */}
                <div className="mt-4 pt-4 border-t border-amber-100">
                  <div className="flex items-start gap-2 text-sm">
                    <MapPin className="h-4 w-4 text-amber-600 mt-0.5" />
                    <div>
                      <p className="font-medium text-amber-900">{order.shipping_address.name}</p>
                      <p className="text-amber-700">
                        {order.shipping_address.address_line1}
                        {order.shipping_address.address_line2 && `, ${order.shipping_address.address_line2}`}
                      </p>
                      <p className="text-amber-700">
                        {order.shipping_address.city}, {order.shipping_address.state} - {order.shipping_address.pincode}
                      </p>
                      <p className="text-amber-700">Phone: {order.shipping_address.phone}</p>
                    </div>
                  </div>
                </div>

                {/* Actions */}
                <div className="mt-4 pt-4 border-t border-amber-100 flex flex-col sm:flex-row sm:justify-end gap-3">

                  <Button
                    onClick={() => navigate(`/order-tracking/${order.order_number}`)}
                    className="bg-amber-600 hover:bg-amber-700 text-white"
                    size="sm"
                  >
                    Track Order
                    <ChevronRight className="h-4 w-4 ml-2" />
                  </Button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {reviewModal.open && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl w-full max-w-lg p-6 shadow-xl">
            <h3 className="text-xl font-bold text-amber-950 mb-1">Share Your Feedback</h3>
            <p className="text-sm text-amber-700 mb-4">
              {reviewModal.productName} • Order #{reviewModal.orderNumber}
            </p>

            <div className="space-y-4">
              <div>
                <p className="text-sm font-medium text-amber-950 mb-2">Rating *</p>
                {renderStars(reviewForm.rating, (v) => setReviewForm((prev) => ({ ...prev, rating: v })))}
              </div>

              <div>
                <p className="text-sm font-medium text-amber-950 mb-2">Description (Optional)</p>
                <textarea
                  rows={3}
                  value={reviewForm.comment}
                  onChange={(e) => setReviewForm((prev) => ({ ...prev, comment: e.target.value }))}
                  className="w-full border-2 border-amber-200 rounded-xl p-2.5 bg-white"
                  placeholder="Tell others about your experience"
                />
              </div>

              <div>
                <p className="text-sm font-medium text-amber-950 mb-2">Photos (Optional)</p>
                <Input
                  type="file"
                  multiple
                  accept="image/*"
                  onChange={(e) => setReviewForm((prev) => ({ ...prev, files: Array.from(e.target.files || []) }))}
                />
                {reviewForm.files.length > 0 && (
                  <p className="text-xs text-amber-700 mt-1">{reviewForm.files.length} image(s) selected</p>
                )}
              </div>
            </div>

            <div className="flex flex-col sm:flex-row gap-3 mt-6">
              <Button
                variant="outline"
                className="flex-1 border-amber-300 text-amber-700"
                onClick={closeReviewModal}
              >
                Cancel
              </Button>
              <Button
                className="flex-1 bg-amber-600 hover:bg-amber-700 text-white"
                onClick={handleSubmitReview}
                disabled={reviewLoadingKey === `${reviewModal.orderId}:${reviewModal.productId}`}
              >
                {reviewLoadingKey === `${reviewModal.orderId}:${reviewModal.productId}` ? 'Submitting...' : 'Submit Review'}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default MyOrders;


